/**
 * Display formatting for a city and state/province: title-cased city, two-letter
 * US state or Canadian province. Unknown regions pass through trimmed. Display
 * only, stored addresses are never rewritten.
 */

const REGIONS: Record<string, string> = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA', colorado: 'CO',
  connecticut: 'CT', delaware: 'DE', 'district of columbia': 'DC', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA', kansas: 'KS', kentucky: 'KY',
  louisiana: 'LA', maine: 'ME', maryland: 'MD', massachusetts: 'MA', michigan: 'MI', minnesota: 'MN',
  mississippi: 'MS', missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH',
  'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND',
  ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI',
  'south carolina': 'SC', 'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
  'puerto rico': 'PR',
  alberta: 'AB', 'british columbia': 'BC', manitoba: 'MB', 'new brunswick': 'NB',
  'newfoundland and labrador': 'NL', newfoundland: 'NL', 'nova scotia': 'NS',
  'northwest territories': 'NT', nunavut: 'NU', ontario: 'ON', 'prince edward island': 'PE',
  quebec: 'QC', 'québec': 'QC', saskatchewan: 'SK', yukon: 'YT', 'yukon territory': 'YT',
};

const CODES = new Set(Object.values(REGIONS));

/** "texas" / "Tx" -> "TX"; an unknown value comes back trimmed. */
export function abbreviateRegion(state?: string | null): string {
  const value = String(state ?? '').trim();
  if (!value) return '';
  const upper = value.toUpperCase();
  if (CODES.has(upper)) return upper;
  return REGIONS[value.toLowerCase().replace(/\s+/g, ' ')] ?? value;
}

/** "CHAMBERSBURG" -> "Chambersburg", "st. louis" -> "St. Louis". Small joiners stay lower case. */
export function titleCaseCity(city?: string | null): string {
  const value = String(city ?? '').trim().replace(/\s+/g, ' ');
  if (!value) return '';
  return value
    .toLowerCase()
    .replace(/(^|[\s\-/(])([a-zà-öø-ÿ])/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase())
    .replace(/(\s)(Of|The|De|La|Du)(?=\s)/g, (_m, sp: string, w: string) => sp + w.toLowerCase());
}

/** "Dallas, Texas" / "CHAMBERSBURG, PA" -> "Dallas, TX" / "Chambersburg, PA". Either part may be missing. */
export function formatCityState(city?: string | null, state?: string | null): string {
  return [titleCaseCity(city), abbreviateRegion(state)].filter(Boolean).join(', ');
}
